import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Music, Users, ListMusic } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface CompatibilityDetails {
  score: number;
  details: {
    genreMatch: number;
    artistMatch: number;
    trackMatch: number;
  };
  commonGenres: string[];
  commonArtists: string[];
}

interface CompatibilityScoreProps {
  userId: number;
}

export function CompatibilityScore({ userId }: CompatibilityScoreProps) {
  const { data: compatibility, isLoading } = useQuery<CompatibilityDetails>({
    queryKey: [`/api/users/${userId}/compatibility`],
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Music Taste Compatibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (!compatibility) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Music Taste Compatibility</span>
          <Badge variant={compatibility.score >= 70 ? "default" : "secondary"}>
            {compatibility.score.toFixed(1)}% Match
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <Music className="h-4 w-4" /> Genre Match
            </span>
            <span>{compatibility.details.genreMatch.toFixed(1)}%</span>
          </div>
          <Progress value={compatibility.details.genreMatch} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Artist Match
            </span>
            <span>{compatibility.details.artistMatch.toFixed(1)}%</span>
          </div>
          <Progress value={compatibility.details.artistMatch} />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <ListMusic className="h-4 w-4" /> Track Match
            </span>
            <span>{compatibility.details.trackMatch.toFixed(1)}%</span>
          </div>
          <Progress value={compatibility.details.trackMatch} />
        </div>

        {compatibility.commonGenres.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Common Genres</h4>
            <div className="flex flex-wrap gap-2">
              {compatibility.commonGenres.map(genre => (
                <Badge key={genre} variant="secondary">
                  {genre}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {compatibility.commonArtists.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Common Artists</h4>
            <div className="flex flex-wrap gap-2">
              {compatibility.commonArtists.map(artist => (
                <Badge key={artist} variant="secondary">
                  {artist}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
